import { request } from "@api/request";
import type { BroadcastConfig } from "@lib/broadcast";

export interface LoginRequest {
    email: string;
    password: string;
}

/** Login, /auth/me and /auth/refresh-token all answer with the same session shape. */
export interface SessionResponse {
    message?: string;
    accessToken?: string;
    token?: string;
    tokenType?: string;
    role?: string;
    billingOnly?: boolean;
    user?: Record<string, unknown>;
    permissions?: string[];
    broadcast?: BroadcastConfig;
}

export interface UpdateProfileRequest {
    name: string;
    email?: string;
    phone?: string;
    companyName?: string;
    city?: string;
    address?: string;
}

export interface ChangePasswordRequest {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

export interface ResetPasswordRequest {
    email: string;
    otp: string;
    password: string;
    confirmPassword: string;
}

export interface UserResponse {
    message?: string;
    user: Record<string, unknown>;
}

export const authApi = {
    login: (data: LoginRequest) => request.post<SessionResponse>("/auth/login", data),
    logout: () => request.post<{ message?: string }>("/auth/logout"),
    me: () => request.get<SessionResponse>("/auth/me"),
    checkAuth: () => request.post<SessionResponse>("/auth/check-auth", {}),
    refreshToken: () => request.post<SessionResponse>("/auth/refresh-token"),
    forgotPassword: (email: string) =>
        request.post<{ message: string }>("/auth/forgot-password", { email }),
    verifyOtp: (email: string, otp: string) =>
        request.post<{ message?: string; verified?: boolean }>("/auth/verify-otp", {
            email,
            otp,
        }),
    resetPassword: (data: ResetPasswordRequest) =>
        request.post<{ message: string }>("/auth/reset-password", {
            email: data.email,
            otp: data.otp,
            password: data.password,
            confirm_password: data.confirmPassword,
        }),
    updateProfile: (data: UpdateProfileRequest) =>
        request.post<UserResponse>("/auth/update-profile", data),
    changePassword: (data: ChangePasswordRequest) =>
        request.post<{ message: string }>("/auth/change-password", {
            current_password: data.currentPassword,
            new_password: data.newPassword,
            confirm_password: data.confirmPassword,
        }),
    updateProfilePicture: (file: File) => {
        const formData = new FormData();
        formData.append("profilePicture", file);
        return request.post<UserResponse>("/auth/update-profile-picture", formData);
    },
};
